import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  AppTile,
  PAGINATED_APP_GRID_CONFIG,
  PaginatedAppGrid,
  usePaginatedAppGrid,
} from '@atlassian/mpac-app-tile';
import {
  BackToTopButton,
  BodyNormal,
  FluidContainer,
  MatchingLabelSkeleton,
  Meta,
  NotFound,
  PartialOutageError,
  SortDropdown,
  sortOptionsConfig,
} from '@atlassian/mpac-primitives';
import { useSearchFilters } from '@atlassian/mpac-search';
import { GetCollection } from '@atlassian/mpac-services-client';
import Telemetry from '@atlassian/mpac-telemetry';
import { ApolloErrorHandler, useParams } from '@atlassian/mpac-ui';
import { useEnvironment, useSource } from '@atlassian/mpac-utils';
import { styled } from '@compiled/react';
import React, { useEffect, useLayoutEffect, useState } from 'react';

import { CollectionPageTask } from '../../../telemetry/tasks';
import { Hero } from '../../common/Hero/Hero';
import { HeroSkeleton } from '../../common/Hero/Hero.styles';
import { useCollectionPageAnalytics, useCollectionTelemetry } from './hooks';
import { UseCaseCard } from './UseCaseCard';

import type { TileData } from '@atlassian/mpac-app-tile';
import type {
  CollectionFragment,
  GetCollectionQuery,
  GetCollectionQueryVariables,
} from '@atlassian/mpac-services-client';

const Container = styled.div({
  paddingBottom: token('space.800'),
});

const HeroWrapper = styled.div({
  marginBottom: token('space.500'),
});

export const ErrorContainer = styled.div({
  position: 'absolute',
  width: '100%',
  height: '100%',
  padding: `${token('space.400')}`,
});

const SortAndCountWrapper = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: token('space.500'),
  marginBottom: token('space.200'),

  [media.below.sm]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: token('space.200'),
  },
});

const H2 = ({ children, ...props }) => (
  <BodyNormal as="h2" {...props}>
    {children}
  </BodyNormal>
);

const BodyStyled = styled(H2)({
  fontWeight: 400,
});

// eslint-disable-next-line max-lines-per-function, complexity
const CollectionPage = () => {
  const source = useSource();
  const environment = useEnvironment();
  const { slug } = useParams();
  const { commitSortKey, onCommitSortOptionSelect } = useSearchFilters();

  const {
    totalCount,
    rawData,
    appList,
    isLoading,
    isLoadingMore,
    showLoadMoreButton,
    fetchMoreApps,
    initialFetchError,
    fetchMoreError,
  } = usePaginatedAppGrid<GetCollectionQuery, GetCollectionQueryVariables>({
    query: GetCollection,
    queryOptions: {
      variables: {
        slug: slug ?? '',
        sort: commitSortKey,
        after: 0,
        first: PAGINATED_APP_GRID_CONFIG.initialFetchCount,
      },
    },
    parseResponse: (data) => {
      return {
        appList:
          data?.marketplaceCollection?.apps?.edges?.map((edge) => edge.node as TileData) ?? [],
        hasNextPage: Boolean(data?.marketplaceCollection?.apps?.pageInfo.hasNextPage),
        totalCount: data?.marketplaceCollection?.apps?.pageInfo.totalCount || 0,
      };
    },
  });

  useEffect(() => {
    CollectionPageTask.start();
  }, []);

  // When query variables like 'sort' changes, initial query
  // is re-fetched where collection data is reset by apollo graphql
  // Persisting collection data in local state to avoid showing loader
  // for collection data in such cases which would ideally be picked
  // from cache even though refetched
  const [collection, setCollection] = useState<CollectionFragment | null>(
    rawData?.marketplaceCollection?.collection ?? null
  );
  useEffect(() => {
    if (rawData?.marketplaceCollection?.collection) {
      setCollection(rawData.marketplaceCollection.collection);
    }
  }, [rawData?.marketplaceCollection?.collection]);

  // Track perf for overall page loads & transitions (slug change)
  useLayoutEffect(() => {
    if (totalCount > 0) {
      Telemetry.timeToFirstMeaningfulPaint(performance.now());
    }
  }, [slug, totalCount]);

  useCollectionTelemetry({
    slug,
    initialFetchError,
    collection,
    isLoadingMore,
    fetchMoreError,
    appList: rawData?.marketplaceCollection?.apps,
    sortKey: commitSortKey,
  });

  const { fireOnTileClickEvent, fireOnLoadMoreClickEvent, fireOnSortOptionSelectEvent } =
    useCollectionPageAnalytics({
      slug,
      initialFetchError,
      collection,
      fetchMoreError,
      appsCount: appList?.length || 0,
      sortKey: commitSortKey || sortOptionsConfig?.[0].value,
      isLoading,
      isLoadingMore,
    });

  const handleOnTileClick = (app: TileData, index: number) => {
    const attributes = {
      collectionType: collection?.slug,
      hitRank: index + 1,
    };
    fireOnTileClickEvent(attributes, app);
  };

  const handleOnLoadMoreClick = async () => {
    const attributes = {
      collectionType: collection?.slug,
      pageNumber: Math.ceil(appList.length / PAGINATED_APP_GRID_CONFIG.fetchMoreCount),
    };
    fireOnLoadMoreClickEvent(attributes);

    fetchMoreApps({
      first: PAGINATED_APP_GRID_CONFIG.fetchMoreCount,
      variables: {
        skipCollection: true,
      },
    });
  };

  const handleOnSortOptionSelect = (sortOption) => {
    const attributes = {
      collectionType: collection?.slug,
      filterType: 'sort',
      filterValue: sortOption.value,
    };
    fireOnSortOptionSelectEvent(attributes);

    onCommitSortOptionSelect(sortOption);
  };
  // If slug exist but  there is initialfetcherror and networkerror is 401
  if (slug && initialFetchError) {
    //When slug exists but initialfetcherror is not found that is 404
    if (initialFetchError?.message === 'NOT_FOUND') {
      return <NotFound />;
    }
    return (
      <ApolloErrorHandler
        error={initialFetchError}
        context={{ page: 'CollectionPage', operationName: 'GetCollection' }}
        renderError={() => {
          CollectionPageTask.failed(initialFetchError);
          return (
            <ErrorContainer data-testid="error">
              <PartialOutageError level="global" />
            </ErrorContainer>
          );
        }}
        nonServerErrorCallback={() => CollectionPageTask.abort()}
      />
    );
  }
  //If slug does not exist  or there is initialfetcherror but category is not found or showpageerror is true
  if (!slug || initialFetchError) {
    return (
      <ErrorContainer>
        <PartialOutageError level="global" />
      </ErrorContainer>
    );
  }

  return (
    <Container>
      {collection && (
        <Meta
          title={`${collection.name} apps for Atlassian products`}
          description={collection.heroSection.description}
        />
      )}
      <HeroWrapper>
        {collection ? (
          <Hero
            backgroundColor={collection.heroSection.backgroundColor}
            breadcrumbTitle={collection.name}
            title={collection.heroSection.title}
            description={collection.heroSection.description}
            imageUrl={collection.heroSection.image.url}
            imageAltText={collection.heroSection.image.altText}
          />
        ) : (
          <HeroSkeleton />
        )}
      </HeroWrapper>

      {collection?.useCases ? (
        <UseCaseCard heading={collection.useCases.heading} useCases={collection.useCases.values} />
      ) : null}

      <FluidContainer>
        <SortAndCountWrapper>
          {isLoading ? (
            <MatchingLabelSkeleton />
          ) : (
            <BodyStyled>Showing {totalCount} apps</BodyStyled>
          )}
          <SortDropdown
            onSortOptionSelect={handleOnSortOptionSelect}
            selectedSortKey={commitSortKey}
          />
        </SortAndCountWrapper>

        <PaginatedAppGrid
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          onLoadMoreClick={handleOnLoadMoreClick}
          showLoadMoreButton={showLoadMoreButton}
          renderGrid={() =>
            appList.map((app, index) => (
              <AppTile
                key={index}
                tileData={app}
                environment={environment}
                source={source}
                onTileClick={() => handleOnTileClick(app, index)}
              />
            ))
          }
          fetchMoreError={fetchMoreError}
          initialFetchError={initialFetchError}
        />
      </FluidContainer>
      <BackToTopButton />
    </Container>
  );
};

export default CollectionPage;
